import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { useLocation } from 'react-router-dom';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography
} from '@mui/material';
import { RingLoader } from 'react-spinners';
import PdfPreview from '../../../Common/PDFPreview'
import { formatReadableAmount } from '../../../Common/FormatFunctions/FormatAmount';
import { FormaDateBalanceSheet } from "../../../Common/FormatFunctions/FormatDate"

const apikey = process.env.REACT_APP_API;

const PendingReports = () => {

    const location = useLocation();

    const Company_Name = sessionStorage.getItem('Company_Name')
    const Company_GSTNO = sessionStorage.getItem('Company_GSTNO')

    const searchParams = new URLSearchParams(location.search);
    const fromDate = searchParams.get('fromDate');
    const toDate = searchParams.get('toDate');
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pdfPreview, setPdfPreview] = useState(null);


    const API_URL = `${apikey}/pendingreport-tenderwisesauda`;

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear());
        return `${day}/${month}/${year}`;
    };

    useEffect(() => {
        const fetchReportData = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await axios.get(API_URL, {
                    params: {
                        from_date: fromDate,
                        to_date: toDate,
                    },
                });
                setReportData(response.data);
            } catch (error) {
                console.error('Error fetching report:', error);
                setError('Error fetching report');
            } finally {
                setLoading(false);
            }
        };

        fetchReportData();
    }, [API_URL]);

    const handleExportToExcel = () => {
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(reportData);
        XLSX.utils.book_append_sheet(wb, ws, 'Pending Reports');
        XLSX.writeFile(wb, 'PendingReports.xlsx');
    };


    const handlePrint = () => {
        const groupedData = groupReportData(reportData);

        let printWindow = window.open('', '', 'height=900,width=1200');

        const formatRow = (cells, isHeader = false) => {
            return `<tr>
                ${cells.map((cell, index) => {
                let align = 'left';
                if ([0, 1, 2, 4, 10, 11].includes(index)) align = 'center';
                else if ([6, 7, 9].includes(index)) align = 'right';

                const style = isHeader
                    ? `background-color:#b4b4b4;font-weight:bold;text-align:${align};padding:4px;border:1px solid #ccc;`
                    : `text-align:${align};padding:4px;border:1px solid #ccc;`;

                return `<td style="${style}">${cell ?? ''}</td>`;
            }).join('')}
            </tr>`;
        };

        let content = `
            <html>
            <head>
                <style>
                    body { font-family: Arial, sans-serif; font-size: 12px; color: #000; }
                    h2, h4 { text-align: center; margin: 4px 0; }
                    table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                    th, td { border: 1px solid #ccc; padding: 4px; font-size: 11px; }
                    .group-header { background-color: #dce6f1; font-weight: bold; color: #000; text-align: center; padding: 6px; }
                    .total-row td { font-weight: bold; background-color: #eaeaea; }
                </style>
            </head>
            <body>
                <h2>${Company_Name}</h2>
                <h4>Tender Wise Sauda - From Date: ${formatDate(fromDate)} To Date: ${formatDate(toDate)}</h4>
        `;

        Object.entries(groupedData).forEach(([key, group]) => {
            const [mc, millName] = key.split('-');
            content += `<div class="group-header">${mc} - ${millName}</div>`;
            content += `<table>
                <thead>
                    ${formatRow([
                'Sauda Date', 'Tender No', 'ID', 'Customer Name', 'Season', 'Grade',
                'Sale Rate', 'Mill Rate', 'Party Name', 'Quantity', 'Dispatch Type', 'Payment Date'
            ], true)}
                </thead>
                <tbody>`;

            group.items.forEach(item => {
                content += formatRow([
                    formatDate(item.Sauda_Date),
                    item.Tender_No,
                    item.ID,
                    item.CustomerName,
                    item.season,
                    item.Grade,
                    formatReadableAmount(item.Sale_Rate),
                    formatReadableAmount(item.Mill_Rate),
                    item.PartyName,
                    formatReadableAmount(item.Qty),
                    item.DispatchType || '',
                    formatDate(item.PaymentDate),
                ]);
            });

            content += `
                <tr class="total-row">
                    <td colspan="9"></td>
                    <td style="text-align:right;">${group.totalQty.toFixed(2)}</td>
                    <td colspan="2"></td>
                </tr>
                </tbody>
            </table>`;
        });

        content += `
            </body>
            </html>
        `;

        printWindow.document.write(content);
        printWindow.document.close();
        printWindow.print();
    };


    const generatePDF = async () => {
        const doc = new jsPDF('l');
        doc.setFontSize(10);
        const topMargin = 10;
        let currentY = topMargin;

        const pageWidth = doc.internal.pageSize.width;
        const companyX = pageWidth / 2;

        doc.text(`${Company_Name}`, companyX, currentY, null, null, "center");
        currentY += 5;

        doc.setFontSize(8);
        const reportTitle = `Tender Wise Sauda - From Date : ${formatDate(fromDate)} To Date : ${formatDate(toDate)}`;
        const titleWidth = doc.getStringUnitWidth(reportTitle) * doc.internal.getFontSize() / doc.internal.scaleFactor;
        doc.text(reportTitle, (pageWidth - titleWidth) / 2, currentY);
        currentY += 4;

        const groupedData = groupReportData(reportData);
        const tableBody = [];

        Object.entries(groupedData).forEach(([key, group]) => {
            const [mc, millName] = key.split('-');

            tableBody.push([{
                content: `${mc} - ${millName}`,
                colSpan: 12,
                styles: {
                    halign: 'center',
                    fontStyle: 'bold',
                    textColor: [0, 0, 0],
                    fillColor: [220, 230, 241],
                    fontSize: 7
                }
            }]);

            group.items.forEach(item => {
                tableBody.push([
                    { content: formatDate(item.Sauda_Date), styles: { halign: 'center' } },
                    { content: item.Tender_No || "", styles: { halign: 'center' } },
                    { content: item.ID || "", styles: { halign: 'center' } },
                    { content: item.CustomerName || "", styles: { halign: 'left' } },
                    { content: item.season || "", styles: { halign: 'center' } },
                    { content: item.Grade || "", styles: { halign: 'left' } },
                    { content: formatReadableAmount(item.Sale_Rate) || "", styles: { halign: 'right' } },
                    { content: formatReadableAmount(item.Mill_Rate) || "", styles: { halign: 'right' } },
                    { content: item.PartyName || "", styles: { halign: 'left' } },
                    { content: formatReadableAmount(item.Qty) || "", styles: { halign: 'right' } },
                    { content: item.DispatchType || "", styles: { halign: 'center' } },
                    { content: formatDate(item.PaymentDate), styles: { halign: 'center' } },
                ]);
            });

            tableBody.push([
                { content: '', colSpan: 9 },
                { content: group.totalQty.toFixed(2), styles: { halign: 'right', fontStyle: 'bold' } },
                { content: '', colSpan: 2 }
            ]);
        });

        doc.autoTable({
            head: [[
                'Sauda Date', 'Tender No', 'ID', 'Customer Name', 'Season', 'Grade',
                'Sale Rate', 'Mill Rate', 'Party Name', 'Quantity', 'Dispatch Type', 'Payment Date'
            ]],
            body: tableBody,
            startY: currentY + 2,
            theme: 'grid',
            styles: {
                fontSize: 5,
                cellPadding: 0.6,
                overflow: 'visible',
                lineWidth: 0.1
            },
            columnStyles: {
                0: { halign: 'center' },
                1: { halign: 'center', fontSize: 6 },
                2: { halign: 'center', fontSize: 6 },
                3: { halign: 'left' },
                4: { halign: 'center' },
                5: { halign: 'left' },
                6: { halign: 'right' },
                7: { halign: 'right' },
                8: { halign: 'left' },
                9: { halign: 'right' },
                10: { halign: 'center' },
                11: { halign: 'center' },
            },
            headStyles: {
                fillColor: [180, 180, 180],
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                lineWidth: 0.1
            },
            bodyStyles: {
                textColor: [0, 0, 0],
                lineColor: [0, 0, 0],
                lineWidth: 0.1
            },
            footStyles: {
                fillColor: [200, 200, 200],
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                fontSize: 6
            }
        });

        const pdfBlob = doc.output("blob");
        const pdfUrl = URL.createObjectURL(pdfBlob);
        setPdfPreview(pdfUrl);
    };


    const groupReportData = (data) => {
        const groupedData = {};
        data.forEach((item) => {
            const key = `${item.mc}-${item.MillName}`;
            if (!groupedData[key]) {
                groupedData[key] = {
                    items: [],
                    totalQty: 0,
                };
            }
            groupedData[key].items.push(item);
            groupedData[key].totalQty += parseFloat(item.Qty) || 0;
        });
        return groupedData;
    };

    const groupedReportData = groupReportData(reportData);

    return (
        <div style={{marginTop:"-80px"}}>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "24px", fontWeight: "bold" }}>{Company_Name}</Typography>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "16px", textDecoration: 'underline', fontWeight: "550" }}>GSTN : {Company_GSTNO}</Typography>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "20px", fontWeight: "bold" }}>Tender Wise Sauda</Typography>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "16px" }}>{FormaDateBalanceSheet(fromDate)} to {FormaDateBalanceSheet(toDate)}</Typography>

            <div className="mb-3 row align-items-center">
                <div className="col-auto">
                    <button className="btn btn-secondary me-2" onClick={handlePrint}>Print</button>
                    <button className="btn btn-success me-2" onClick={handleExportToExcel}>Export to Excel</button>
                    {pdfPreview && <PdfPreview pdfData={pdfPreview} apiData={reportData[0]} label={"TenderWiseSauda"} />}
                    <button className="btn btn-success" onClick={generatePDF}>PDF Preview</button>
                </div>
            </div>

            <TableContainer component={Paper} style={{ maxHeight: '70vh', overflow: 'auto' }}>
                <Table stickyHeader size="small" aria-label="Tender Wise Sauda Table">
                    <TableHead>
                        <TableRow>
                            {[
                                "Sauda Date", "Tender No", "ID", "Customer Name", "Season", "Grade",
                                "Sale Rate", "Mill Rate", "Party Name", "Quintal", "Dispatch Type", "Payment Date"
                            ].map((header, i) => (
                                <TableCell
                                    key={i}
                                    sx={{
                                        position: 'sticky',
                                        top: 0,
                                        backgroundColor: '#f5f5f5',
                                        fontWeight: 'bold',
                                        whiteSpace: header.includes("Tender") ? "nowrap" : "normal",
                                        textAlign: i === 2 || i === 0 ? 'center' : i > 5 && i <= 9 ? 'right' : 'left',
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    {header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {Object.entries(groupedReportData).map(([key, { items, totalQty }]) => {
                            const [mc, millName] = key.split('-');
                            return (
                                <React.Fragment key={key}>
                                    <TableRow>
                                        <TableCell colSpan={12} sx={{ color: 'blue', fontWeight: 'bold' }}>
                                            {mc} - {millName}
                                        </TableCell>
                                    </TableRow>

                                    {items.map((item, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{formatDate(item.Sauda_Date)}</TableCell>
                                            <TableCell>{item.Tender_No}</TableCell>
                                            <TableCell align="center">{item.ID}</TableCell>
                                            <TableCell align="left">{item.CustomerName}</TableCell>
                                            <TableCell>{item.season}</TableCell>
                                            <TableCell>{item.Grade}</TableCell>
                                            <TableCell align="right">{formatReadableAmount(item.Sale_Rate)}</TableCell>
                                            <TableCell align="right">{formatReadableAmount(item.Mill_Rate)}</TableCell>
                                            <TableCell align="left">{item.PartyName}</TableCell>
                                            <TableCell align="right">{formatReadableAmount(item.Qty)}</TableCell>
                                            <TableCell align="center">{item.DispatchType}</TableCell>
                                            <TableCell>{formatDate(item.PaymentDate)}</TableCell>
                                        </TableRow>
                                    ))}

                                    <TableRow>
                                        <TableCell colSpan={9} align="right" sx={{ fontWeight: 'bold' }}></TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                            {formatReadableAmount(totalQty)}
                                        </TableCell>
                                        <TableCell colSpan={2}></TableCell>
                                    </TableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {loading && (
                <div style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 9999
                }}>
                    <RingLoader size={80} />
                </div>
            )}

            {error && <div className="alert alert-danger">{error}</div>}
        </div>
    );
};

export default PendingReports;
